import { Button, Grid } from "@mui/material";
import { useState } from "react";
import { downloadFromApiPost } from "../../common/download";
import { createEntityStore } from "../../common/entityStore";
import { propDateField, propEnumField, propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { propRequired, propValidValue } from "../../common/entityStoreValidators";
import { EnumItemDto, orderTypes } from "../../common/enum";
import { createListStore } from "../../common/listStore";
import { ClearButton } from "../../common/listStoreUi";
import { PageTitle } from "../../layout/PageTitle";
import { useParams } from "react-router-dom";
import { getResource, useLocalizedPropValidators, useResources } from "../../common/localization";
import { combineLatest, map } from "rxjs";
import { useAccountStore, UserTypeId } from "../../common/account";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.OperatorReport." + key, defaultMessage) as string;
}

export function TmOperatorsReportPage() {
    const urlParams = useParams();
    const resources = useResources();
    return resources.ready ? <TmOperatorsReportPageInternal key={urlParams.id} /> : <></>;
}

export function TmOperatorsReportPageInternal() {
    const isAdmin = useAccountStore(p => p.data?.accessTokenDecoded.userTypeId === UserTypeId.Admin);
    const [useStore] = useState(() => createEntityStore<TmOrdersReportParamsDto>({
        data: getNewData(),
        isValidationEnabled: true,
        propValidators: [
            isAdmin && propRequired("callCenterId"),
            propRequired("callCenterProjectId"),
            propValidValue("fromDate", "fromDateFormatted"),
            propValidValue("toDate", "toDateFormatted"),
        ]
    }));
    useLocalizedPropValidators(useStore);

    const { isSaveDisabled } = useStore(p => ({ isSaveDisabled: p.isSaveDisabled }));
    const [isExporting, setIsExporting] = useState(false);

    function getNewData(): TmOrdersReportParamsDto {
        return {
            callCenterId: null,
            fromDate: null,
            toDate: null,
            callCenterProjectId: null,
            orderType: "",
            mediaCode: "",
        };
    }
    function clear() {
        useStore.getState().setClientData(getNewData());
    }
    async function doExport() {
        useStore.getState().enableValidation();

        if (useStore.getState().isSaveDisabled) {
            return;
        }

        try {
            setIsExporting(true);
            await downloadFromApiPost(`reports/tmOperatorsReport/xlsx`, useStore.getState().clientData);
        } finally {
            setIsExporting(false);
        }
    }

    const [useCallCentersStore] = useState(() => createListStore<CallCenterWithTmProjectsDto>({
        path: "reports/tmOperatorsReport/callCenters",
    }));

    const [useTmProjectStore] = useState(() => createListStore<EnumItemDto>({
        data: combineLatest([
            useCallCentersStore.getState().items$,
            useStore.getState().clientData$.pipe(map(p => p.data.callCenterId)),
        ]).pipe(map(([callCenters, callCenterId]) => {
            const callCenter = callCenters.items.length === 1 ? callCenters.items[0] : callCenters.items.find(p => p.id === callCenterId);
            return {
                items: callCenter ? callCenter.callCenterProjects : [],
                isFetching: callCenters.isFetching,
            };
        })),
    }));

    return <>
        <PageTitle title={getText("OperatorResultsOverview")} />
        <Grid container columnSpacing={2}>
            {isAdmin && <Grid item width={250}>{propEnumField(useStore)("callCenterId", getResource("Ui.CallCenter") as string, useCallCentersStore, {
                autoComplete: true,
                onChange: () => useStore.getState().setClientDataProp("callCenterProjectId", null),
            })}</Grid>}
            <Grid item width={400}>{propEnumField(useStore)("callCenterProjectId", getText("ProjectName"), useTmProjectStore, { autoComplete: true })}</Grid>
            <Grid item width={110}>{propTextField(useStore)("mediaCode", getText("MediaCode"))}</Grid>
            <Grid item width={150}>{propSimpleEnumField(useStore)("orderType", getText("OrderType"), orderTypes, { addAll: true })}</Grid>
            <Grid item width={150}>{propDateField(useStore)("fromDate", "fromDateFormatted", getText("FromDate"))}</Grid>
            <Grid item width={150}>{propDateField(useStore)("toDate", "toDateFormatted", getText("ToDate"))}</Grid>
            <ClearButton onClick={clear} disabled={isExporting} />
            <Grid item xs={12}><Button onClick={doExport} disabled={isExporting || isSaveDisabled}>{getResource("Ui.Export", "Export")}</Button></Grid>
        </Grid>
    </>;
}

interface TmOrdersReportParamsDto {
    callCenterId: number | null;
    fromDate: string | null;
    toDate: string | null;
    callCenterProjectId: number | null;
    orderType: string;
    mediaCode: string;
    mailIdFormatted?: string;
    fromDateFormatted?: string;
    toDateFormatted?: string;
};

interface CallCenterWithTmProjectsDto {
    id: number;
    name: string;
    callCenterProjects: EnumItemDto[];
}